/* HC custom — the header search: the computer dropdown under the search bar (hc-search-dropdown)
   and the search panel for phones and tablets (hc-search-phone). Markup comes from
   snippets/hc-search-bar.liquid, hc-search-phone.liquid and hc-search-start.liquid.

   Before typing, both show what Liquid already rendered: the Trending searches and a few products
   (and on phones, the shopper's recently viewed products). While typing, they ask Shopify's
   predictive search for suggestions and products and draw them in the same shapes - SearchView
   does that part for both. The search box is a plain GET form, so Enter always opens the full
   results page, script or not.

   Computers. Closing: Esc, a click anywhere outside, Tab past the last link, a wheel over the faded
   page, or the header scrolling away. Clicks inside the dropdown keep focus in the bar (mousedown
   is cancelled), so Safari - which does not focus links on click - never sees a blur that would
   hide the panel before the click. Touch and pen input can still blur the box mid-tap, so a press
   inside holds it open.

   Phones and tablets: Horizon gives every touch device the drawer header, where the computer bar
   hides. There the search icon (or the bar under the logo) opens a modal <dialog>: a slide-up
   sheet, a drop-down under the header, or the full screen. In the top layer the header's layout
   containment can't trap it, and the page behind is inert and scroll-locked. It opens and focuses
   its box in the same tap, so phones raise the keyboard straight away. Closing: Cancel, a tap
   outside, Esc (or Android's back), a swipe down (sheet), or coming back to the page from another
   one. */

import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const DEBOUNCE_MS = 180;
const CACHE_SIZE = 30;
const PHOTO_WIDTHS = [150, 200, 300, 400, 500, 600];
/** A sheet dragged down this far, or flicked down at this speed (px per ms), closes. */
const SWIPE_CLOSE_PX = 96;
const SWIPE_CLOSE_SPEED = 0.5;
/** The longest closing transition, with a little room. */
const CLOSE_MS = 240;
/** The sheet's rise (280ms in hc-search-phone.liquid), with a little room. */
const RISE_MS = 300;
const RECENT_PLACEHOLDER =
  '<span class="hc-search-dd__card hc-search-dd__card--loading" aria-hidden="true"><span class="hc-search-dd__img"></span><span class="hc-search-dd__line"></span><span class="hc-search-dd__line hc-search-dd__line--short"></span></span>';

let uid = 0;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {unknown} value */
const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character
  );

/**
 * Same-site path from a link Shopify sent back, or '' if it points anywhere else.
 * @param {unknown} url
 */
function sameSitePath(url) {
  if (typeof url !== 'string' || !url) return '';
  try {
    const parsed = new URL(url, location.origin);
    return parsed.origin === location.origin ? parsed.pathname + parsed.search : '';
  } catch (error) {
    return '';
  }
}

/**
 * @param {string} url
 * @param {number} width
 */
function photoUrl(url, width) {
  try {
    const parsed = new URL(url, location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch (error) {
    return url;
  }
}

/**
 * Like Liquid's money_without_trailing_zeros: $139, $109.95.
 * @param {unknown} amount
 */
function money(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    const text = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value);
    return text.replace(/[.,]00(?=\D*$)/, '');
  } catch (error) {
    return `$${value.toFixed(2).replace(/\.00$/, '')}`;
  }
}

/**
 * Arrow keys walk a panel's links; Up from the first one goes back to the search box.
 * @param {KeyboardEvent} event
 * @param {HTMLAnchorElement[]} links
 * @param {() => void} toInput
 */
function moveThroughLinks(event, links, toInput) {
  const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
  if (!step) return;
  const index = links.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
  if (index < 0) return;
  event.preventDefault();
  const next = links[index + step];
  if (next) next.focus();
  else if (step < 0) toInput();
}

/**
 * What both searches share: the before-typing view, and Shopify's suggestions and products while
 * typing, drawn into the results box. Words, counts and photo sizes come from the host element's
 * data attributes.
 */
class SearchView {
  /** @type {AbortController | null} */ #request = null;
  /** @type {Map<string, { products: any[], queries: any[] }>} */ #cache = new Map();
  #timer = 0;
  #icon = '';

  /**
   * @param {{ host: HTMLElement, form: HTMLFormElement, input: HTMLInputElement, clear: HTMLButtonElement | null,
   *   busy: HTMLElement, start: HTMLElement, results: HTMLElement, status: HTMLElement | null }} parts
   */
  constructor(parts) {
    this.host = parts.host;
    this.form = parts.form;
    this.input = parts.input;
    this.clear = parts.clear;
    this.busy = parts.busy;
    this.start = parts.start;
    this.results = parts.results;
    this.status = parts.status;
    this.#icon = parts.form.querySelector('.hc-search-bar__icon svg')?.outerHTML ?? '';
  }

  get count() {
    return Number(this.host.dataset.count) || 3;
  }

  /** There is something to show before typing. */
  get hasStart() {
    return Boolean(this.start.querySelector('a'));
  }

  term() {
    return this.input.value.trim();
  }

  syncClear() {
    if (this.clear) this.clear.hidden = !this.input.value;
  }

  /**
   * Shows what fits the box now: results for its words, or the before-typing view.
   * @returns {boolean} Whether there is anything to show.
   */
  refresh() {
    // Safari can restore an old value into the box when the shopper comes back to a page.
    this.syncClear();
    const term = this.term();
    if (term) {
      const cached = this.#cache.get(term.toLowerCase());
      if (cached) this.#render(term, cached);
      else this.#search(term);
      return true;
    }
    if (!this.hasStart) return false;
    this.showStart();
    return true;
  }

  /**
   * After each keystroke: the before-typing view once the box is empty, otherwise results for the
   * words after a short pause.
   * @returns {boolean} Whether there is anything to show.
   */
  update() {
    this.syncClear();
    clearTimeout(this.#timer);
    const term = this.term();
    if (!term) {
      this.#request?.abort();
      if (!this.hasStart) return false;
      this.showStart();
      return true;
    }
    const cached = this.#cache.get(term.toLowerCase());
    if (cached) this.#render(term, cached);
    else this.#timer = window.setTimeout(() => this.#search(term), DEBOUNCE_MS);
    return true;
  }

  /**
   * Empties the box.
   * @returns {boolean} Whether there is anything to show before typing.
   */
  reset() {
    this.input.value = '';
    this.stop();
    this.syncClear();
    if (!this.hasStart) return false;
    this.showStart();
    return true;
  }

  stop() {
    clearTimeout(this.#timer);
    this.#request?.abort();
  }

  showStart() {
    this.results.hidden = true;
    this.results.replaceChildren();
    this.start.hidden = false;
    if (this.status) this.status.textContent = '';
  }

  /** @returns {HTMLAnchorElement[]} */
  links() {
    const view = this.results.hidden ? this.start : this.results;
    return [...view.querySelectorAll('a[href]')];
  }

  /** @param {string} term */
  searchUrl(term) {
    const params = new URLSearchParams(new FormData(this.form));
    params.set('q', term);
    return `${this.form.getAttribute('action') || '/search'}?${params}`;
  }

  /** @param {string} term */
  async #search(term) {
    const key = term.toLowerCase();
    const cached = this.#cache.get(key);
    if (cached) {
      this.#render(term, cached);
      return;
    }

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    const url = new URL(this.host.dataset.suggestUrl || '/search/suggest.json', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query');
    // Shopify sends at most 10 of each.
    url.searchParams.set('resources[limit]', String(Math.min(10, Math.max(this.count, 3) + 1)));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    this.busy.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Search suggestions failed: ${response.status}`);
      const data = await response.json();
      const found = data?.resources?.results ?? {};
      const result = {
        products: Array.isArray(found.products) ? found.products : [],
        queries: Array.isArray(found.queries) ? found.queries : [],
      };
      this.#cache.set(key, result);
      if (this.#cache.size > CACHE_SIZE) this.#cache.delete(this.#cache.keys().next().value);
      if (this.term().toLowerCase() === key) this.#render(term, result);
    } catch (error) {
      if (request.signal.aborted) return;
      if (this.term().toLowerCase() === key) this.#render(term, null);
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.busy.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {string} term
   * @param {{ products: any[], queries: any[] } | null} result - null when suggestions could not load.
   */
  #render(term, result) {
    const data = this.host.dataset;
    const lower = term.toLowerCase();
    const count = this.count;
    const queries = (result?.queries ?? [])
      .filter((query) => typeof query?.text === 'string' && query.text.trim().toLowerCase() !== lower)
      .slice(0, Number(data.queries) || (count >= 4 ? 4 : 3));
    const products = (result?.products ?? []).filter((product) => product?.title).slice(0, count);
    const allUrl = this.searchUrl(term);
    // Phones lay products out in rows that swipe sideways together: 8 in 2 rows is 2 rows of 4.
    const rows = Number(data.rows) || 0;
    const cardsStyle = rows > 0 ? ` style="--hc-search-cols: ${Math.max(1, Math.ceil(products.length / rows))}"` : '';
    let html = '';

    if (!result) {
      // Suggestions did not load: offer the full search for what they typed.
      html = `<div class="hc-search-dd__chips">${this.#chip(term, allUrl, '')}</div>`;
    } else if (!queries.length && !products.length) {
      const note = (data.noResults || '').replace('[terms]', term);
      html = `<p class="hc-search-dd__note">${escapeHtml(note)}</p>`;
      html += this.start.querySelector('[data-trending]')?.outerHTML ?? '';
    } else {
      if (queries.length) {
        html += `<p class="hc-search-dd__h">${escapeHtml(data.suggestionsTitle)}</p>`;
        html += `<div class="hc-search-dd__chips">${queries
          .map((query) => this.#chip(query.text, sameSitePath(query.url) || this.searchUrl(query.text), term))
          .join('')}</div>`;
      }
      if (products.length) {
        html += `<div class="hc-search-dd__row"><p class="hc-search-dd__h">${escapeHtml(data.productsTitle)}</p>`;
        html += `<a class="hc-search-dd__all" href="${escapeHtml(allUrl)}">${escapeHtml(data.viewAll)}</a></div>`;
        html += `<div class="hc-search-dd__cards"${cardsStyle}>${products.map((product) => this.#card(product)).join('')}</div>`;
      }
    }

    this.results.innerHTML = html;
    this.results.hidden = false;
    this.start.hidden = true;

    if (this.status) {
      const shown = queries.length + products.length;
      const message = shown ? data.statusCount || '' : data.statusNone || '';
      this.status.textContent = message.replace('[count]', String(shown)).replace('[query]', term);
    }
  }

  /**
   * A suggestion chip: what they typed in grey, the rest of the suggestion in bold.
   * @param {string} text
   * @param {string} url
   * @param {string} typed
   */
  #chip(text, url, typed) {
    const at = typed ? text.toLowerCase().indexOf(typed.toLowerCase()) : -1;
    const label =
      at < 0
        ? escapeHtml(text)
        : `${escapeHtml(text.slice(0, at))}<span class="hc-search-dd__typed">${escapeHtml(
            text.slice(at, at + typed.length)
          )}</span><b>${escapeHtml(text.slice(at + typed.length))}</b>`;
    return `<a class="hc-search-dd__chip" href="${escapeHtml(url)}">${this.#icon}<span>${label}</span></a>`;
  }

  /** @param {any} product */
  #card(product) {
    const url = sameSitePath(product.url) || `/products/${encodeURIComponent(product.handle ?? '')}`;
    const image = product.featured_image?.url || product.image || '';
    const price = Number(product.price_min ?? product.price);
    const compare = Number(product.compare_at_price_min ?? product.compare_at_price_max);
    const priceHtml =
      compare > price
        ? `<span class="hc-search-dd__sale">${escapeHtml(money(price))}</span> <s>${escapeHtml(money(compare))}</s>`
        : escapeHtml(money(price));
    const sizes = this.host.dataset.cardSizes || `${Number(this.host.dataset.cardSize) || 140}px`;
    const photo = image
      ? `<img src="${escapeHtml(photoUrl(image, 400))}" srcset="${escapeHtml(
          PHOTO_WIDTHS.map((width) => `${photoUrl(image, width)} ${width}w`).join(', ')
        )}" sizes="${escapeHtml(sizes)}" alt="" decoding="async">`
      : '';
    return `<a class="hc-search-dd__card" href="${escapeHtml(url)}"><span class="hc-search-dd__img">${photo}</span><span class="hc-search-dd__title">${escapeHtml(
      product.title
    )}</span><span class="hc-search-dd__price">${priceHtml}</span></a>`;
  }
}

/** Computers: the dropdown under the header's search bar. */
class HcSearchDropdown extends HTMLElement {
  /** @type {HTMLFormElement | null} */ #form = null;
  /** @type {HTMLInputElement | null} */ #input = null;
  /** @type {HTMLElement | null} */ #panel = null;
  /** @type {SearchView | null} */ #view = null;
  /** @type {HTMLElement | null} */ #fade = null;
  /** @type {{ element: HTMLElement, zIndex: string, position: string } | null} */ #raised = null;
  #mobile = window.matchMedia('(max-width: 749px)');
  #open = false;
  #quiet = false;
  #frame = 0;
  #pressing = false;
  #pressTimer = 0;
  #placeholder = '';

  connectedCallback() {
    const form = this.querySelector('form');
    const input = this.querySelector('.hc-search-bar__input');
    const panel = this.querySelector('.hc-search-dd');
    const start = this.querySelector('[data-start]');
    const results = this.querySelector('[data-results]');
    if (
      !(form instanceof HTMLFormElement) ||
      !(input instanceof HTMLInputElement) ||
      !(panel instanceof HTMLElement) ||
      !(start instanceof HTMLElement) ||
      !(results instanceof HTMLElement)
    ) {
      return;
    }
    const clear = this.querySelector('.hc-search-bar__clear');
    const status = this.querySelector('[data-status]');
    this.#form = form;
    this.#input = input;
    this.#panel = panel;
    this.#view = new SearchView({
      host: this,
      form,
      input,
      clear: clear instanceof HTMLButtonElement ? clear : null,
      busy: panel,
      start,
      results,
      status: status instanceof HTMLElement ? status : null,
    });

    // Unique ids in case a page ever shows the bar twice.
    panel.id = `HcSearchDropdown-${++uid}`;
    input.setAttribute('aria-controls', panel.id);
    this.#placeholder = input.placeholder;

    input.addEventListener('focus', this.#onFocus);
    input.addEventListener('click', this.#onFocus);
    input.addEventListener('input', this.#onInput);
    input.addEventListener('keydown', this.#onInputKeydown);
    form.addEventListener('mousedown', this.#onFormMousedown);
    form.addEventListener('submit', this.#onSubmit);
    clear?.addEventListener('click', this.#onClear);
    panel.addEventListener('mousedown', this.#keepFocus);
    panel.addEventListener('keydown', this.#onPanelKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerenter', this.#warmPhotos, { once: true });
    this.addEventListener('focusin', this.#warmPhotos, { once: true });
    this.#mobile.addEventListener('change', this.#onBreakpoint);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#view.syncClear();
  }

  disconnectedCallback() {
    this.#close();
    clearTimeout(this.#pressTimer);
    this.#view?.stop();
    this.#mobile.removeEventListener('change', this.#onBreakpoint);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onFocus = () => {
    if (this.#quiet) return;
    this.#show();
  };

  #onInput = () => {
    if (!this.#view) return;
    if (!this.#view.update()) this.#close();
    else if (!this.#view.term() || document.activeElement === this.#input) this.#openPanel();
  };

  /** @param {KeyboardEvent} event */
  #onInputKeydown = (event) => {
    if (event.key === 'Escape') {
      // Chrome would also empty the box; Esc only closes the dropdown here.
      event.preventDefault();
      this.#close();
    } else if (event.key === 'ArrowDown') {
      if (!this.#open) this.#show();
      const first = this.#view?.links()[0];
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /**
   * A click on the bar's icon or padding types into the box; the clear button keeps focus where it is.
   * @param {MouseEvent} event
   */
  #onFormMousedown = (event) => {
    if (event.target === this.#input) return;
    event.preventDefault();
    if (event.target instanceof Element && event.target.closest('.hc-search-bar__clear')) return;
    this.#input?.focus();
    this.#show();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#view?.term()) return;
    event.preventDefault();
    this.#focusInput();
  };

  #onClear = () => {
    if (!this.#view) return;
    const showing = this.#view.reset();
    this.#focusInput();
    if (showing) this.#openPanel();
    else this.#close();
  };

  /** @param {MouseEvent} event */
  #keepFocus = (event) => {
    event.preventDefault();
  };

  /** @param {KeyboardEvent} event */
  #onPanelKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      this.#focusInput();
      return;
    }
    moveThroughLinks(event, this.#view?.links() ?? [], () => this.#focusInput());
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    // Wait for focus to land: switching windows keeps focus here, Tab away does not.
    requestAnimationFrame(() => {
      if (this.#pressing) return;
      if (!this.contains(document.activeElement)) this.#close();
    });
  };

  /** A press inside: touch or pen can blur the box before the click lands, so stay open for it. */
  #onPointerDown = () => {
    this.#pressing = true;
    clearTimeout(this.#pressTimer);
    this.#pressTimer = window.setTimeout(() => {
      this.#pressing = false;
    }, 800);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close();
  };

  /** @param {Event} [event] */
  #onScroll = (event) => {
    if (event?.target instanceof Node && this.#panel?.contains(event.target)) return;
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#place);
  };

  #onBreakpoint = () => {
    if (this.#mobile.matches) this.#close();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#close();
    this.#view?.syncClear();
  };

  /** Start loading the before-typing photos once the shopper heads for the bar. */
  #warmPhotos = () => {
    this.#view?.start.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  };

  #show() {
    if (this.#view?.refresh()) this.#openPanel();
  }

  #openPanel() {
    if (this.#open || !this.#panel || !this.#input || this.#mobile.matches) return;
    this.#open = true;
    this.#panel.hidden = false;
    if (this.dataset.prompt) this.#input.placeholder = this.dataset.prompt;
    if (this.hasAttribute('data-fade')) this.#addFade();
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#place();
  }

  #close() {
    if (!this.#open) return;
    this.#open = false;
    if (this.#panel) this.#panel.hidden = true;
    if (this.#input) this.#input.placeholder = this.#placeholder;
    this.#removeFade();
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** Keep the fade under the header and the dropdown inside the window; close if the header leaves. */
  #place = () => {
    this.#frame = 0;
    if (!this.#open || !this.#form || !this.#panel) return;
    const bar = this.#form.getBoundingClientRect();
    if (bar.bottom <= 0 || bar.height === 0) {
      this.#close();
      return;
    }
    const top = this.#panel.getBoundingClientRect().top;
    this.#panel.style.maxBlockSize = `${Math.max(240, Math.floor(window.innerHeight - top - 16))}px`;
    if (this.#fade) {
      const header = this.closest('#header-component') ?? this.closest('header') ?? this.#form;
      this.#fade.style.top = `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`;
    }
  };

  #addFade() {
    if (this.#fade) return;
    // The fade sits one layer under the header's own layer, so it covers the page but never the header.
    const section = /** @type {HTMLElement | null} */ (this.closest('.shopify-section') ?? this.closest('header'));
    let layer = 18;
    if (section) {
      const style = getComputedStyle(section);
      const zIndex = Number.parseInt(style.zIndex, 10);
      if (Number.isFinite(zIndex) && zIndex > 1) {
        layer = zIndex;
      } else {
        this.#raised = { element: section, zIndex: section.style.zIndex, position: section.style.position };
        section.style.zIndex = String(layer);
        if (style.position === 'static') section.style.position = 'relative';
      }
    }
    const fade = document.createElement('div');
    fade.className = 'hc-search-dd-fade';
    fade.setAttribute('aria-hidden', 'true');
    fade.style.zIndex = String(layer - 1);
    // Scrolling the faded page means the shopper is done searching.
    fade.addEventListener('wheel', () => this.#close(), { passive: true });
    fade.addEventListener('touchmove', () => this.#close(), { passive: true });
    document.body.append(fade);
    this.#fade = fade;
  }

  #removeFade() {
    this.#fade?.remove();
    this.#fade = null;
    if (this.#raised) {
      this.#raised.element.style.zIndex = this.#raised.zIndex;
      this.#raised.element.style.position = this.#raised.position;
      this.#raised = null;
    }
  }

  /** Put focus back in the box without reopening the dropdown. */
  #focusInput() {
    this.#quiet = true;
    this.#input?.focus();
    this.#quiet = false;
  }
}

/** Phones and tablets: the search panel the header's search icon or bar opens. */
class HcSearchPhone extends HTMLElement {
  /** @type {HTMLDialogElement | null} */ #dialog = null;
  /** @type {HTMLElement | null} */ #panel = null;
  /** @type {HTMLElement | null} */ #body = null;
  /** @type {HTMLElement | null} */ #handle = null;
  /** @type {HTMLInputElement | null} */ #input = null;
  /** @type {HTMLInputElement | null} */ #standin = null;
  /** @type {HTMLElement | null} */ #recent = null;
  /** @type {SearchView | null} */ #view = null;
  /** @type {HTMLElement | null} */ #trigger = null;
  /** @type {{ startY: number, lastY: number, lastTime: number, speed: number, offset: number } | null} */ #drag = null;
  /** @type {{ x: number, y: number, top: boolean, decided: boolean, pulling: boolean, blocked: boolean } | null} */ #touch = null;
  #closing = false;
  #closeTimer = 0;
  #handOffTimer = 0;
  #locked = false;
  #scrollTop = 0;
  #warmed = false;
  #recentIds = '';

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const panel = this.querySelector('.hc-search-pp__panel');
    const body = this.querySelector('.hc-search-pp__body');
    const form = this.querySelector('form');
    const input = this.querySelector('.hc-search-bar__input');
    const start = this.querySelector('[data-start]');
    const results = this.querySelector('[data-results]');
    if (
      !(dialog instanceof HTMLDialogElement) ||
      !(panel instanceof HTMLElement) ||
      !(body instanceof HTMLElement) ||
      !(form instanceof HTMLFormElement) ||
      !(input instanceof HTMLInputElement) ||
      !(start instanceof HTMLElement) ||
      !(results instanceof HTMLElement)
    ) {
      return;
    }
    const clear = this.querySelector('.hc-search-bar__clear');
    const status = this.querySelector('[data-status]');
    const handle = this.querySelector('.hc-search-pp__handle');
    const recent = this.querySelector('[data-recent]');
    const standin = this.querySelector('.hc-search-pp__standin');
    this.#dialog = dialog;
    this.#panel = panel;
    this.#body = body;
    this.#input = input;
    this.#standin = standin instanceof HTMLInputElement ? standin : null;
    this.#handle = handle instanceof HTMLElement ? handle : null;
    this.#recent = recent instanceof HTMLElement ? recent : null;
    this.#view = new SearchView({
      host: this,
      form,
      input,
      clear: clear instanceof HTMLButtonElement ? clear : null,
      busy: body,
      start,
      results,
      status: status instanceof HTMLElement ? status : null,
    });

    body.id ||= `HcSearchPhoneResults-${++uid}`;
    input.setAttribute('aria-controls', body.id);

    // Capture: a click handler further down the header can't stop the panel opening.
    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { capture: true, passive: true });
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('close', this.#onDialogClose);
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('keydown', this.#onDialogKeydown);
    dialog.addEventListener('touchmove', this.#onDialogTouchMove, { passive: false });
    input.addEventListener('input', this.#onInput);
    input.addEventListener('keydown', this.#onInputKeydown);
    form.addEventListener('submit', this.#onSubmit);
    clear?.addEventListener('click', this.#onClear);
    this.#handle?.addEventListener('pointerdown', this.#onHandleDown);
    body.addEventListener('touchstart', this.#onBodyTouchStart, { passive: true });
    body.addEventListener('touchmove', this.#onBodyTouchMove, { passive: false });
    body.addEventListener('touchend', this.#onBodyTouchEnd, { passive: true });
    body.addEventListener('touchcancel', this.#onBodyTouchEnd, { passive: true });
    this.querySelector('[data-recent-clear]')?.addEventListener('click', this.#onRecentClear);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#view.syncClear();
    // Tapped before this script ran: hc-search-phone.liquid opened the dialog natively. Take it over.
    if (dialog.open) this.#adopt();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, { capture: true });
    window.removeEventListener('pageshow', this.#onPageShow);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#handOffTimer);
    this.#stopWatching();
    this.#view?.stop();
    if (this.#dialog && this.#locked) unlockScroll(this.#dialog);
    this.#locked = false;
  }

  get #isSheet() {
    return this.dataset.panel === 'sheet';
  }

  /**
   * Opens the panel and puts the cursor in its box - in the same tap, so phones raise the keyboard.
   * @param {HTMLElement} trigger - The search icon or bar that opened it.
   */
  open(trigger) {
    const dialog = this.#dialog;
    const input = this.#input;
    if (!dialog || !input || !this.#view || dialog.open) return;
    this.#trigger = trigger;
    this.#warm();
    this.#place();
    this.#scrollTop = getScrollTop();
    try {
      dialog.showModal();
    } catch (error) {
      return;
    }
    lockScroll(dialog);
    this.#locked = true;
    this.#view.refresh();
    trigger.setAttribute('aria-expanded', 'true');
    this.#watch();
    if (this.#isSheet && this.#standin && !reducedMotion.matches) {
      // The sheet starts below the screen. Its box taking the cursor there would make iOS scroll the
      // whole view down to it, and the sheet would land off the top. The stand-in at the top of the
      // screen takes the cursor (and raises the keyboard) in this tap; the box takes over once the
      // sheet is up.
      this.#standin.focus({ preventScroll: true });
      // Nothing on a sheet still sliding up under the finger takes a tap (a quick Cancel could open a
      // product instead); a tap lands on the dimmed page and closes it.
      dialog.classList.add('is-opening');
      this.#handOffTimer = window.setTimeout(this.#handOff, RISE_MS);
    } else {
      input.focus({ preventScroll: true });
    }
  }

  /** The panel opened natively before this script ran: lock the page and fill it in as if we had opened it. */
  #adopt() {
    const dialog = this.#dialog;
    if (!dialog || !this.#view) return;
    const opener = document.querySelector('[data-hc-search-opened]');
    opener?.removeAttribute('data-hc-search-opened');
    this.#trigger = opener instanceof HTMLElement ? opener : document.querySelector('[data-hc-search-open]');
    this.#warm();
    this.#place();
    this.#scrollTop = getScrollTop();
    lockScroll(dialog);
    this.#locked = true;
    this.#view.refresh();
    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.#watch();
    if (document.activeElement === this.#standin) this.#handOffTimer = window.setTimeout(this.#handOff, RISE_MS);
  }

  /** The sheet is up: the real box takes the cursor, with anything typed into the stand-in. */
  #handOff = () => {
    this.#dialog?.classList.remove('is-opening');
    const input = this.#input;
    const standin = this.#standin;
    if (!input || !standin || !this.#dialog?.open || this.#closing) return;
    const typed = standin.value;
    standin.value = '';
    if (typed) {
      input.value += typed;
      this.#view?.update();
    }
    if (document.activeElement === standin) input.focus({ preventScroll: true });
  };

  /**
   * Closes the panel; it slides or fades away first unless the shopper asked for less motion.
   * @param {{ instant?: boolean }} [options]
   */
  close({ instant = false } = {}) {
    const dialog = this.#dialog;
    if (!dialog?.open || this.#closing) return;
    this.#closing = true;
    this.#input?.blur();
    this.#standin?.blur();
    this.#view?.stop();
    this.#drag = null;
    this.#touch = null;
    dialog.classList.remove('is-dragging');
    if (instant || reducedMotion.matches) {
      this.#finishClose();
      return;
    }
    if (this.#panel) {
      // Stop the opening slide if it is still running; a dragged sheet leaves from where it is.
      this.#panel.style.animation = 'none';
      this.#panel.style.transform = '';
    }
    dialog.classList.add('is-closing');
    this.#closeTimer = window.setTimeout(this.#finishClose, CLOSE_MS);
  }

  #finishClose = () => {
    if (this.#dialog?.open) this.#dialog.close();
    this.#cleanUp();
  };

  /** The browser closed the dialog itself (Android's back can), or we did: tidy up either way. */
  #onDialogClose = () => {
    this.#cleanUp();
  };

  #cleanUp() {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#handOffTimer);
    if (this.#standin) this.#standin.value = '';
    this.#closing = false;
    this.#dialog?.classList.remove('is-closing', 'is-dragging', 'is-opening');
    if (this.#panel) {
      this.#panel.style.animation = '';
      this.#panel.style.transform = '';
    }
    this.#view?.stop();
    this.#stopWatching();
    this.#trigger?.setAttribute('aria-expanded', 'false');
    if (this.#dialog && this.#locked) {
      unlockScroll(this.#dialog);
      this.#locked = false;
      scrollTo({ top: this.#scrollTop, behavior: 'instant' });
    }
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-hc-search-open]') : null;
    if (!(trigger instanceof HTMLElement) || trigger.getAttribute('aria-controls') !== this.#dialog?.id) return;
    event.preventDefault();
    this.open(trigger);
  };

  /**
   * A finger on the icon or bar: start loading photos and recently viewed before the tap lands.
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-hc-search-open]')) this.#warm();
  };

  /** Esc, or Android's back. @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const target = event.target;
    if (target === this.#dialog || (target instanceof Element && target.closest('[data-close]'))) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onDialogKeydown = (event) => {
    moveThroughLinks(event, this.#view?.links() ?? [], () => this.#input?.focus());
  };

  #onInput = () => {
    this.#view?.update();
  };

  /** A keyboard's Down arrow (iPads) steps into the results. @param {KeyboardEvent} event */
  #onInputKeydown = (event) => {
    if (event.key !== 'ArrowDown') return;
    const first = this.#view?.links()[0];
    if (!first) return;
    event.preventDefault();
    first.focus();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#view?.term()) return;
    event.preventDefault();
    this.#input?.focus();
  };

  #onClear = () => {
    this.#view?.reset();
    this.#input?.focus();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.close({ instant: true });
    this.#view?.syncClear();
  };

  #warm() {
    if (!this.#warmed) {
      this.#warmed = true;
      this.#view?.start.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }
    this.#loadRecent();
  }

  /**
   * Drop-down: hang the box under the header; from the bar, put the panel's box over that bar. The
   * dialog follows the visual viewport, so places on the page are measured from its top.
   */
  #place() {
    const dialog = this.#dialog;
    if (!dialog || this.dataset.panel !== 'drop') return;
    const shift = Math.round(window.visualViewport?.offsetTop ?? 0);
    const header = document.querySelector('#header-component');
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) - shift : 0;
    const top = Math.min(Math.max(0, bottom), Math.round(window.innerHeight / 2));
    dialog.style.setProperty('--hc-pp-top', `${top}px`);
    if (this.dataset.start === 'bar') {
      const row = this.#trigger?.closest('.hc-search-row') ?? document.querySelector('.hc-search-row');
      const box = row?.getBoundingClientRect();
      dialog.style.setProperty('--hc-pp-bar-top', `${box ? Math.max(0, Math.round(box.top) - shift) : top}px`);
      dialog.style.setProperty('--hc-pp-bar-gap', `${box ? Math.max(0, top - (Math.round(box.bottom) - shift)) : 0}px`);
    }
  }

  #watch() {
    window.visualViewport?.addEventListener('resize', this.#onViewport);
    window.visualViewport?.addEventListener('scroll', this.#onViewport);
    window.addEventListener('resize', this.#onResize);
    this.#onViewport();
  }

  #stopWatching() {
    window.visualViewport?.removeEventListener('resize', this.#onViewport);
    window.visualViewport?.removeEventListener('scroll', this.#onViewport);
    window.removeEventListener('resize', this.#onResize);
    this.#dialog?.style.removeProperty('--hc-pp-view-top');
    this.#dialog?.style.removeProperty('--hc-pp-covered');
  }

  /**
   * The keyboard (and, on iOS 26, Safari's see-through address and Done bars above it) covers the
   * bottom of the panel. The panel runs on underneath them, like an app, with that much room below
   * the results so they all scroll clear. If iOS has scrolled its view, the dialog follows - but only
   * while something covers the bottom: iOS 26 can leave a stale offset after the keyboard closes
   * (WebKit bug 297779).
   */
  #onViewport = () => {
    const viewport = window.visualViewport;
    const dialog = this.#dialog;
    if (!viewport || !dialog) return;
    // The dialog is the window's height and starts at the top of the view, so whatever the view
    // leaves out at the bottom is what the keyboard and bars cover.
    const covered = Math.max(0, Math.round(window.innerHeight - viewport.height));
    const top = covered > 1 ? Math.max(0, Math.round(viewport.offsetTop)) : 0;
    dialog.style.setProperty('--hc-pp-view-top', `${top}px`);
    dialog.style.setProperty('--hc-pp-covered', `${covered > 1 ? covered : 0}px`);
    this.#place();
  };

  #onResize = () => {
    if (!this.#dialog?.open) return;
    // Turned or stretched into the computer header: the icon or bar is gone, so the panel goes too.
    if (this.#trigger && !this.#trigger.getClientRects().length) {
      this.close({ instant: true });
      return;
    }
    this.#place();
  };

  /** @param {PointerEvent} event */
  #onHandleDown = (event) => {
    if (!this.#isSheet || this.#closing || event.button > 0 || !this.#handle) return;
    event.preventDefault();
    this.#beginDrag(event.clientY);
    this.#handle.setPointerCapture(event.pointerId);
    this.#handle.addEventListener('pointermove', this.#onHandleMove);
    this.#handle.addEventListener('pointerup', this.#onHandleUp);
    this.#handle.addEventListener('pointercancel', this.#onHandleUp);
  };

  /** @param {PointerEvent} event */
  #onHandleMove = (event) => {
    this.#moveDrag(event.clientY);
  };

  #onHandleUp = () => {
    this.#handle?.removeEventListener('pointermove', this.#onHandleMove);
    this.#handle?.removeEventListener('pointerup', this.#onHandleUp);
    this.#handle?.removeEventListener('pointercancel', this.#onHandleUp);
    this.#endDrag();
  };

  /**
   * Drags outside the results (the dimmed page, the handle, the search row): nothing there scrolls,
   * and while the keyboard is up iOS would pan the whole page instead, locked or not.
   * @param {TouchEvent} event
   */
  #onDialogTouchMove = (event) => {
    const target = event.target;
    if (target instanceof Node && this.#body?.contains(target)) return;
    if (target instanceof HTMLInputElement) return;
    if (event.cancelable) event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #onBodyTouchStart = (event) => {
    if (this.#closing || event.touches.length !== 1 || !this.#body) return;
    const touch = event.touches[0];
    this.#touch = { x: touch.clientX, y: touch.clientY, top: this.#body.scrollTop <= 0, decided: false, pulling: false, blocked: false };
  };

  /**
   * The first move of a drag on the results decides what it does, before iOS starts a scroll of its own:
   * - Down from the top of the sheet's results: it pulls the sheet down instead.
   * - Up or down where the results can't go any further: blocked. iOS would pan the page behind
   *   (it does that while the keyboard is up, locked or not).
   * - Anything else scrolls the results, and sideways swipes scroll a row of products.
   * @param {TouchEvent} event
   */
  #onBodyTouchMove = (event) => {
    const state = this.#touch;
    const body = this.#body;
    if (!state || !body || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - state.x;
    const dy = touch.clientY - state.y;
    if (!state.decided) {
      state.decided = true;
      const upright = Math.abs(dy) >= Math.abs(dx);
      const atTop = body.scrollTop <= 0;
      const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 1;
      state.pulling = this.#isSheet && state.top && atTop && dy > 0 && upright;
      state.blocked = !state.pulling && upright && ((dy > 0 && atTop) || (dy < 0 && atEnd));
      if (state.pulling) this.#beginDrag(state.y);
      // While the keyboard is up, iOS pans the page under the panel instead of scrolling the results
      // (WebKit gives the gesture to the page it moved for the keyboard). So a drag puts the keyboard
      // away first, like a phone app, and the next one scrolls.
      const keyboard = window.innerHeight - (window.visualViewport?.height ?? window.innerHeight) > 1;
      const typing = document.activeElement === this.#input || document.activeElement === this.#standin;
      if (upright && !state.pulling && keyboard && typing) {
        state.blocked = true;
        this.#input?.blur();
        this.#standin?.blur();
      }
    }
    if (state.pulling) {
      if (event.cancelable) event.preventDefault();
      this.#moveDrag(touch.clientY);
    } else if (state.blocked && event.cancelable) {
      event.preventDefault();
    }
  };

  #onBodyTouchEnd = () => {
    if (this.#touch?.pulling) this.#endDrag();
    this.#touch = null;
  };

  /** @param {number} y */
  #beginDrag(y) {
    this.#drag = { startY: y, lastY: y, lastTime: performance.now(), speed: 0, offset: 0 };
    this.#dialog?.classList.add('is-dragging');
  }

  /** @param {number} y */
  #moveDrag(y) {
    const drag = this.#drag;
    if (!drag || !this.#panel) return;
    const now = performance.now();
    drag.speed = (y - drag.lastY) / Math.max(1, now - drag.lastTime);
    drag.lastY = y;
    drag.lastTime = now;
    drag.offset = Math.max(0, y - drag.startY);
    this.#panel.style.transform = drag.offset ? `translateY(${drag.offset}px)` : '';
  }

  #endDrag() {
    const drag = this.#drag;
    this.#drag = null;
    this.#dialog?.classList.remove('is-dragging');
    if (!drag || !this.#panel) return;
    if (drag.offset > SWIPE_CLOSE_PX || (drag.offset > 24 && drag.speed > SWIPE_CLOSE_SPEED)) {
      this.close();
    } else {
      // Not far enough: the sheet settles back up.
      this.#panel.style.transform = '';
    }
  }

  /**
   * Recently viewed: Horizon keeps the ids of the last few products a shopper opened on this device
   * (assets/recently-viewed-products.js). Grey stand-ins hold their place while the cards come from
   * sections/hc-search-products.liquid, so nothing jumps when they land.
   */
  async #loadRecent() {
    const row = this.#recent;
    const cards = row?.querySelector('[data-recent-cards]');
    if (!row || !(cards instanceof HTMLElement)) return;

    /** @type {unknown} */
    let stored = [];
    try {
      const { RecentlyViewed } = await import('@theme/recently-viewed-products');
      stored = RecentlyViewed.getProducts();
    } catch (error) {
      stored = [];
    }
    const current = this.dataset.currentProduct;
    const ids = (Array.isArray(stored) ? stored : []).map(String).filter((id) => /^\d+$/.test(id) && id !== current);
    const key = ids.join(',');
    if (key === this.#recentIds) return;
    this.#recentIds = key;
    if (!ids.length) {
      this.#hideRecent();
      return;
    }

    cards.style.setProperty('--hc-search-cols', String(ids.length));
    cards.innerHTML = ids.map(() => RECENT_PLACEHOLDER).join('');
    row.hidden = false;

    try {
      const url = new URL(this.dataset.recentUrl || '/search', location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', 'hc-search-products');
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Recently viewed failed: ${response.status}`);
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (this.#recentIds !== key) return;
      const found = [...markup.querySelectorAll('[data-recent-items] .hc-search-dd__card')];
      if (!found.length) {
        this.#hideRecent();
        return;
      }
      const sizes = this.dataset.cardSizes || '150px';
      for (const card of found) {
        for (const image of card.querySelectorAll('img')) {
          image.sizes = sizes;
          image.loading = 'eager';
        }
      }
      cards.style.setProperty('--hc-search-cols', String(found.length));
      cards.replaceChildren(...found.map((card) => document.importNode(card, true)));
    } catch (error) {
      if (this.#recentIds !== key) return;
      this.#hideRecent();
      // Try again next time the panel opens.
      this.#recentIds = '';
    }
  }

  #hideRecent() {
    if (!this.#recent) return;
    this.#recent.hidden = true;
    this.#recent.querySelector('[data-recent-cards]')?.replaceChildren();
  }

  #onRecentClear = async () => {
    try {
      const { RecentlyViewed } = await import('@theme/recently-viewed-products');
      RecentlyViewed.clearProducts();
    } catch (error) {
      // Nothing stored, or storage is off.
    }
    this.#recentIds = '';
    this.#hideRecent();
    // The button is gone; keep focus in the panel without raising the keyboard.
    this.#body?.focus({ preventScroll: true });
  };
}

if (!customElements.get('hc-search-dropdown')) {
  customElements.define('hc-search-dropdown', HcSearchDropdown);
}

if (!customElements.get('hc-search-phone')) {
  customElements.define('hc-search-phone', HcSearchPhone);
}
